/**
 * Display-resolution rounding for the conviction a field `select` hands to a
 * feed consumer (MOR-887).
 *
 * The SDK's conviction projector rewrites each cached outcome's
 * full-precision `conviction` every tick, so a leaf subscribing to the
 * `outcomes` field re-renders on every tick even when the *displayed* value
 * never moves. The canonical display helper computes the visible 2-decimal
 * percent as `round(ppb / 1e5) / 100` (see `convictionPercent`) — so the shown
 * value only changes when the PPB crosses a `1e5` boundary. Rounding conviction
 * to the nearest `1e5` PPB inside the `select` therefore preserves the
 * displayed value EXACTLY while making the selected `outcomes` array
 * `replaceEqualDeep`-equal to the previous tick's whenever the display is
 * unchanged — so TanStack returns the previous reference and the leaf bails.
 *
 * The cache keeps full precision; only this per-consumer display projection
 * rounds, so any surface that genuinely needs raw conviction is unaffected.
 */

import { CONVICTION_SCALE_PPB } from './conviction.js';

/** 2-dp-percent display step: 0.01% = 1e5 PPB (scale ÷ 10_000). */
const DISPLAY_STEP_PPB: bigint = CONVICTION_SCALE_PPB / 10_000n;
const HALF_STEP_PPB: bigint = DISPLAY_STEP_PPB / 2n;

/** Round a PPB conviction string to the nearest display step. Non-numeric /
 *  negative inputs pass through untouched (conviction is a non-negative int). */
function roundConvictionToDisplayStep(conviction: string): string {
  let n: bigint;
  try {
    n = BigInt(conviction);
  } catch {
    return conviction;
  }
  if (n < 0n) return conviction;
  const rounded = ((n + HALF_STEP_PPB) / DISPLAY_STEP_PPB) * DISPLAY_STEP_PPB;
  return rounded === n ? conviction : rounded.toString();
}

/**
 * Round every outcome's `conviction` to display resolution. Returns the SAME
 * reference when the input isn't a projectable outcomes array or nothing
 * rounds differently, and reuses unchanged outcome element references, so it's
 * a no-op ref-wise on rows that don't carry conviction.
 */
export function roundOutcomeConvictionForDisplay(value: unknown): unknown {
  if (!Array.isArray(value)) return value;
  let changed = false;
  const next = value.map((outcome) => {
    if (outcome === null || typeof outcome !== 'object') return outcome;
    const conviction = (outcome as { conviction?: unknown }).conviction;
    if (typeof conviction !== 'string') return outcome;
    const rounded = roundConvictionToDisplayStep(conviction);
    if (rounded === conviction) return outcome;
    changed = true;
    return { ...(outcome as Record<string, unknown>), conviction: rounded };
  });
  return changed ? next : value;
}
